import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useUiStore } from "../stores/ui";
import { workOrdersApi } from "../api/queries/work_orders";
import { dashboardApi } from "../api/queries/companies";
import { StatusDot, workOrderTone } from "../components/StatusDot";
import { formatRelative } from "../lib/time";

const STATUSES = [
  "all",
  "draft",
  "planning",
  "dispatched",
  "in_progress",
  "complete",
  "failed",
  "cancelled",
] as const;
type StatusFilter = (typeof STATUSES)[number];

export function WorkOrdersPage() {
  const companyId = useUiStore((s) => s.activeCompanyId);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [requesterFilter, setRequesterFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  const orders = useQuery({
    queryKey: ["work-orders", companyId, 200],
    enabled: !!companyId,
    queryFn: () => workOrdersApi.list(companyId!, { limit: 200 }),
    refetchInterval: 10_000,
  });

  const members = useQuery({
    queryKey: ["members", companyId],
    queryFn: () => dashboardApi.members(companyId!),
    enabled: !!companyId,
  });
  const memberById = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of members.data?.members ?? []) m.set(x.user_id, x.display_name);
    return m;
  }, [members.data]);

  const filtered = useMemo(() => {
    const all = orders.data?.orders ?? [];
    return all.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (requesterFilter && o.requested_by_user_id !== requesterFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (
          !o.title.toLowerCase().includes(s) &&
          !o.description.toLowerCase().includes(s)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [orders.data, statusFilter, requesterFilter, search]);

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-6">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 className="text-[20px] font-semibold tracking-[-0.01em]">Work orders</h1>
        <span className="text-[12px] text-tertiary">
          {filtered.length} of {orders.data?.orders.length ?? 0}
        </span>
      </header>

      <div className="card p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Status filter">
          {STATUSES.map((s) => (
            <button
              key={s}
              role="tab"
              type="button"
              aria-selected={statusFilter === s}
              className={`btn btn--sm ${
                statusFilter === s ? "btn--secondary" : "btn--ghost"
              }`}
              onClick={() => setStatusFilter(s)}
            >
              {s === "all" ? "All" : s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="search"
            className="input input--sm"
            placeholder="Search title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 180 }}
          />
          <select
            className="input input--sm"
            value={requesterFilter}
            onChange={(e) => setRequesterFilter(e.target.value)}
          >
            <option value="">Any requester</option>
            {(members.data?.members ?? []).map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {orders.isLoading && (
        <div className="grid place-items-center py-10">
          <span className="spinner" />
        </div>
      )}

      {orders.data && filtered.length === 0 && (
        <div className="card px-6 py-12 text-center text-secondary">
          No work orders match the current filter.
        </div>
      )}

      {filtered.length > 0 && (
        <ul className="card divide-y divide-border-subtle">
          {filtered.map((o) => {
            const tone = workOrderTone(o.status);
            const requester =
              memberById.get(o.requested_by_user_id) ||
              o.requested_by_email ||
              "—";
            return (
              <li key={o.id}>
                <Link
                  to="/work-orders/$id"
                  params={{ id: o.id }}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-raised"
                >
                  <StatusDot tone={tone} pulse={tone === "running"} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-medium text-primary">
                      {o.title}
                    </div>
                    {o.description && (
                      <div className="truncate text-[12px] text-tertiary">
                        {o.description}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-tertiary shrink-0 w-[120px] truncate" title={requester}>
                    {requester}
                  </span>
                  <span className="font-mono text-[11px] text-tertiary shrink-0 w-[90px]">
                    {o.status}
                  </span>
                  <span className="font-mono text-[11px] text-tertiary shrink-0 w-[100px] text-right">
                    {formatRelative(o.created_at)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
